"use client";
import React, { useEffect, useState } from "react";
import { useRouter } from "next/navigation";
import Image from "next/image";
import { Box, Typography, TextField, IconButton, InputAdornment, CircularProgress, Button, Divider } from "@mui/material";
import {
  IconUser,
  IconLock,
  IconEye,
  IconEyeOff,
  IconAlertCircle,
  IconHelpCircle,
  IconClock,
} from "@tabler/icons-react";
import axios from "axios";
import { ApiBaseUrl, setCookieForOneDay } from "@/utils/Consts/ApiConsts";
import { designTokens } from "@/utils/theme/tokens";
import { consumirAviso, iniciarLoginZoho, TipoAviso } from "@/utils/Consts/ZohoSso";

interface LoginData {
  usuario: string;
  contrasena: string;
}

const LogInitialState: LoginData = { usuario: "", contrasena: "" };

// Estilo de los campos (label arriba + TextField con borde 12px y adornments).
const fieldSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "12px",
    backgroundColor: "#fff",
    fontSize: 14,
  },
  "& .MuiFormHelperText-root.Mui-error": { color: "#c7563b", fontSize: 12 },
};

const AuthLogin: React.FC = () => {
  const router = useRouter();
  const [loading, setLoading] = useState(false);
  const [logData, setLogData] = useState<LoginData>(LogInitialState);
  const [errorUsuario, setErrorUsuario] = useState(false);
  const [errorContrasena, setErrorContrasena] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  // Banner de error de credenciales/sesión (reemplaza los Snackbars). "pendiente" es
  // el aviso de cuenta sin aprobar: se muestra en ámbar y sin la animación de error.
  const [errorBanner, setErrorBanner] = useState("");
  const [tipoBanner, setTipoBanner] = useState<TipoAviso>("error");
  const [loadingZoho, setLoadingZoho] = useState(false);

  const mostrarAviso = (mensaje: string, tipo: TipoAviso = "error") => {
    setTipoBanner(tipo);
    setErrorBanner(mensaje);
  };

  // Aviso que deja el callback de Zoho al volver al login (pendiente, dominio, etc.).
  useEffect(() => {
    const aviso = consumirAviso();
    if (aviso) mostrarAviso(aviso.mensaje, aviso.tipo);
  }, []);

  const handleLoginZoho = async () => {
    try {
      setLoadingZoho(true);
      await iniciarLoginZoho();
    } catch (error) {
      console.error("Error al iniciar sesión con Zoho:", error);
      mostrarAviso("El inicio de sesión con Zoho no está disponible en este momento.");
      setLoadingZoho(false);
    }
  };

  const handleLogin = async () => {
    if (!logData.usuario || !logData.contrasena) {
      if (!logData.usuario) setErrorUsuario(true);
      if (!logData.contrasena) setErrorContrasena(true);
      return;
    }

    try {
      setLoading(true);
      const authResponse = await axios.post(`${ApiBaseUrl}backoffice/login`, {
        username: logData.usuario,
        password: logData.contrasena,
      });
      if (authResponse.data.exist === true) {
        setCookieForOneDay("bo-auth", authResponse.data.token);
        router.push("/dashboardlayout");
      } else if (authResponse.data.pendiente === true) {
        mostrarAviso(authResponse.data.message, "pendiente");
      } else if (authResponse.data.exist === false) {
        // La API manda `message` cuando las credenciales son correctas pero la cuenta no
        // puede entrar (p. ej. usuario inactivo); el genérico queda para clave errónea.
        mostrarAviso(
          authResponse.data.message ??
            "Usuario o contraseña incorrectos. Verifique sus credenciales."
        );
      }
    } catch (error) {
      console.error("Error al iniciar sesión:", error);
      mostrarAviso("No se pudo iniciar sesión. Intente de nuevo más tarde.");
    } finally {
      setLoading(false);
    }
  };

  const handleUsuarioChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setLogData((prev) => ({ ...prev, usuario: e.target.value }));
    setErrorUsuario(false);
    setErrorBanner("");
  };

  const handleContrasenaChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setLogData((prev) => ({ ...prev, contrasena: e.target.value }));
    setErrorContrasena(false);
    setErrorBanner("");
  };

  return (
    <Box
      component="form"
      onSubmit={(e) => {
        e.preventDefault();
        handleLogin();
      }}
      sx={{
        width: "100%",
        maxWidth: 392,
        animation: "fadeUp .4s ease",
        "@keyframes fadeUp": {
          from: { opacity: 0, transform: "translateY(10px)" },
          to: { opacity: 1, transform: "none" },
        },
      }}
    >
      <Typography
        sx={{
          fontSize: 12,
          fontWeight: 700,
          letterSpacing: ".08em",
          textTransform: "uppercase",
          color: designTokens.color.primary.main,
        }}
      >
        Bienvenido de nuevo
      </Typography>
      <Typography sx={{ fontSize: 27, fontWeight: 800, color: designTokens.color.text.primary, mt: 1 }}>
        Iniciar sesión
      </Typography>
      <Typography sx={{ fontSize: 14, color: designTokens.color.text.muted, mt: "7px" }}>
        Ingrese sus credenciales para acceder al panel.
      </Typography>

      {/* Banner de error (credenciales / sesión) con shake */}
      {errorBanner && (
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            gap: 1.25,
            mt: 3,
            p: "12px 15px",
            backgroundColor: tipoBanner === "pendiente" ? designTokens.color.status.warning.bg : "#ffede9",
            border: `1px solid ${tipoBanner === "pendiente" ? designTokens.color.status.warning.main : "#fbd3c8"}`,
            borderRadius: "12px",
            animation: tipoBanner === "pendiente" ? "none" : "shake .5s",
            "@keyframes shake": {
              "10%, 90%": { transform: "translateX(-1px)" },
              "20%, 80%": { transform: "translateX(2px)" },
              "30%, 50%, 70%": { transform: "translateX(-4px)" },
              "40%, 60%": { transform: "translateX(4px)" },
            },
          }}
        >
          <Box
            sx={{
              color: tipoBanner === "pendiente" ? designTokens.color.status.warning.text : "#e07a5f",
              display: "flex",
              flexShrink: 0,
              mt: "1px",
            }}
          >
            {tipoBanner === "pendiente" ? <IconClock size={18} /> : <IconAlertCircle size={18} />}
          </Box>
          <Typography
            sx={{
              fontSize: 13,
              fontWeight: 600,
              color: tipoBanner === "pendiente" ? designTokens.color.status.warning.text : "#b8533a",
            }}
          >
            {errorBanner}
          </Typography>
        </Box>
      )}

      {/* Usuario */}
      <Box sx={{ mt: 3 }}>
        <Typography
          component="label"
          htmlFor="usuario"
          sx={{ display: "block", fontSize: 12.5, fontWeight: 600, color: designTokens.color.text.secondary, mb: 1 }}
        >
          Usuario
        </Typography>
        <TextField
          id="usuario"
          fullWidth
          placeholder="Ingrese su usuario"
          value={logData.usuario}
          onChange={handleUsuarioChange}
          error={errorUsuario}
          helperText={errorUsuario ? "Ingrese su usuario." : ""}
          autoComplete="username"
          sx={fieldSx}
          slotProps={{ input: {
            startAdornment: (
              <InputAdornment position="start">
                <IconUser size={18} color={designTokens.color.text.faint} />
              </InputAdornment>
            ),
          } }}
        />
      </Box>

      {/* Contraseña */}
      <Box sx={{ mt: "18px" }}>
        <Typography
          component="label"
          htmlFor="contrasena"
          sx={{ display: "block", fontSize: 12.5, fontWeight: 600, color: designTokens.color.text.secondary, mb: 1 }}
        >
          Contraseña
        </Typography>
        <TextField
          id="contrasena"
          fullWidth
          type={showPassword ? "text" : "password"}
          placeholder="Ingrese su contraseña"
          value={logData.contrasena}
          onChange={handleContrasenaChange}
          error={errorContrasena}
          helperText={errorContrasena ? "Ingrese su contraseña." : ""}
          autoComplete="current-password"
          sx={fieldSx}
          slotProps={{ input: {
            startAdornment: (
              <InputAdornment position="start">
                <IconLock size={18} color={designTokens.color.text.faint} />
              </InputAdornment>
            ),
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  onClick={() => setShowPassword((s) => !s)}
                  edge="end"
                  aria-label={showPassword ? "Ocultar contraseña" : "Mostrar contraseña"}
                  sx={{ color: designTokens.color.text.faint, "&:hover": { color: designTokens.color.text.secondary } }}
                >
                  {showPassword ? <IconEyeOff size={19} /> : <IconEye size={19} />}
                </IconButton>
              </InputAdornment>
            ),
          } }}
        />
      </Box>

      {/* Botón principal */}
      <Button
        type="submit"
        fullWidth
        loading={loading}
        loadingIndicator={
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, color: "#fff" }}>
            <CircularProgress size={18} thickness={5} sx={{ color: "#fff" }} />
            <span>Ingresando…</span>
          </Box>
        }
        variant="contained"
        sx={{
          mt: "26px",
          py: "13px",
          borderRadius: "13px",
          fontSize: 15,
          fontWeight: 700,
          boxShadow: "0 8px 20px rgba(0,133,219,0.28)",
          "&:hover": { backgroundColor: designTokens.color.primary.hover },
        }}
      >
        Iniciar sesión
      </Button>

      {/* Zoho One: cuentas corporativas. Ícono oficial (Logosoft es partner de Zoho). */}
      <Divider sx={{ mt: "22px", fontSize: 12, color: designTokens.color.text.faint }}>o</Divider>
      <Button
        fullWidth
        variant="outlined"
        onClick={handleLoginZoho}
        disabled={loadingZoho || loading}
        startIcon={
          loadingZoho ? (
            <CircularProgress size={18} thickness={5} />
          ) : (
            <Image src="/images/logos/zoho-one.png" alt="" width={22} height={22} />
          )
        }
        sx={{
          mt: "18px",
          py: "12px",
          borderRadius: "13px",
          fontSize: 14.5,
          fontWeight: 700,
          textTransform: "none",
        }}
      >
        {loadingZoho ? "Redirigiendo a Zoho…" : "Continuar con Zoho One"}
      </Button>

      {/* Ayuda inferior */}
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 0.75, mt: "22px" }}>
        <IconHelpCircle size={16} color={designTokens.color.text.faint} />
        <Typography sx={{ fontSize: 13, color: designTokens.color.text.faint }}>
          ¿Olvidó su contraseña? Contacte al administrador.
        </Typography>
      </Box>
    </Box>
  );
};

export default AuthLogin;
